import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import { startLoading, stopLoading, setTaskMessage, LOADING_TYPES } from "../loading";
import { canAccessSalesDataImport } from "../features/salesDataImport/access";
import SalesImportNav from "../features/salesDataImport/components/SalesImportNav";
import {
  SALES_IMPORT_BRANCH_OPTIONS,
  salesImportBranchDisplayName,
} from "../features/salesDataImport/config/salesImportBranchIds";
import {
  ITEMS_ON_SALE_ALL_GROUPS,
  ITEMS_ON_SALE_DEFAULT_BRANCH_ID,
  fetchItemsOnSaleDailyReport,
} from "../features/salesDataImport/services/itemsOnSaleDailyReportService";
import { loadMasterGroupNames } from "../features/salesDataImport/services/brandGroupBranchReportService";
import {
  exportItemsOnSaleDailyReportExcel,
  fm,
  formatItemsOnSaleDayLabel,
} from "../features/salesDataImport/services/itemsOnSaleDailyReportExportService";
import { formatFirebaseError } from "../features/salesDataImport/utils/firebaseError";
import "./SalesDataImportCenter.css";
import "./ItemsOnSaleDailyReportPage.css";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function defaultFilters() {
  const now = new Date();
  return {
    year: now.getFullYear(),
    monthIndex: now.getMonth(),
    branchId: ITEMS_ON_SALE_DEFAULT_BRANCH_ID,
    groupName: ITEMS_ON_SALE_ALL_GROUPS,
  };
}

export default function ItemsOnSaleDailyReportPage() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const [dark, setDark] = useState(() => localStorage.getItem("sdic-dark") === "1");
  const [draft, setDraft] = useState(defaultFilters);
  const [applied, setApplied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [expandedDays, setExpandedDays] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [masterGroups, setMasterGroups] = useState([]);

  useEffect(() => {
    let cancelled = false;
    loadMasterGroupNames()
      .then((names) => {
        if (!cancelled) setMasterGroups(names);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, []);

  const groupOptions = useMemo(() => {
    const merged = new Set([...masterGroups, ...(report?.groupOptions || [])]);
    if (draft.groupName && draft.groupName !== ITEMS_ON_SALE_ALL_GROUPS) {
      merged.add(draft.groupName);
    }
    return [...merged].sort((a, b) => a.localeCompare(b));
  }, [masterGroups, report?.groupOptions, draft.groupName]);

  const loadReport = useCallback(async (filters) => {
    if (!filters?.branchId) {
      toast.error("Select a branch.");
      return;
    }
    setLoading(true);
    const taskId = startLoading({
      type: LOADING_TYPES.REPORT,
      report: true,
      message: "Loading daily items on sale…",
    });
    try {
      const result = await fetchItemsOnSaleDailyReport({
        year: filters.year,
        monthIndex: filters.monthIndex,
        branchId: filters.branchId,
        groupName: filters.groupName,
        onProgress: (rows, chunks) => {
          setTaskMessage(
            taskId,
            chunks
              ? `Loaded ${rows.toLocaleString("en-IN")} lines (${chunks} chunks)…`
              : `Loaded ${rows.toLocaleString("en-IN")} lines…`
          );
        },
      });
      setReport(result);
      setApplied(filters);
      const daysWithSales = (result.days || []).filter((d) => d.items.length).map((d) => d.ymd);
      setExpandedDays(new Set(daysWithSales.slice(0, 5)));
      if (!result.meta.scannedLines) {
        toast.error("No import lines found for this month — upload sales data in Import Center first.");
      } else if (!result.meta.matchedLines) {
        toast.error(
          result.meta.groupLabel !== "All groups"
            ? "No sale lines matched this group on working days. Try All groups or another month."
            : "No sale lines on working days for this branch/month."
        );
      } else {
        toast.success(`Loaded ${result.meta.matchedLines.toLocaleString()} sale lines.`);
      }
    } catch (err) {
      console.error(err);
      toast.error(formatFirebaseError(err) || err?.message || "Failed to load report.");
      setReport(null);
    } finally {
      setLoading(false);
      stopLoading(taskId);
    }
  }, []);

  useEffect(() => {
    loadReport(defaultFilters());
  }, [loadReport]);

  const onSearch = () => loadReport(draft);

  const toggleDay = (ymd) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(ymd)) next.delete(ymd);
      else next.add(ymd);
      return next;
    });
  };

  const expandAll = () => {
    if (!report?.days?.length) return;
    setExpandedDays(new Set(report.days.filter((d) => d.items.length).map((d) => d.ymd)));
  };

  const collapseAll = () => setExpandedDays(new Set());

  const filteredDays = useMemo(() => {
    if (!report?.days) return [];
    const q = search.trim().toLowerCase();
    if (!q) return report.days;
    return report.days
      .map((day) => {
        const items = day.items.filter((item) => {
          const hay = `${item.partNo} ${item.itemName} ${item.groupName}`.toLowerCase();
          return hay.includes(q);
        });
        return { ...day, items };
      })
      .filter((day) => day.items.length > 0 || !q);
  }, [report, search]);

  if (!canAccessSalesDataImport(user)) {
    return (
      <div className="flex min-h-screen flex-col">
        <MenuBar />
        <main className="p-6 text-sm text-slate-600">You do not have access to sales import reports.</main>
      </div>
    );
  }

  return (
    <div className={`flex min-h-screen flex-col${dark ? " sdic-dark" : ""}`}>
      <MenuBar />
      <main className="sdic-page ios-daily-sale-page">
        <header className="sdic-header">
          <div>
            <h1 className="sdic-title">Daily Items on Sale</h1>
            <p className="sdic-subtitle">
              Outward supply items sold on each working day (Mon–Sat, excluding Sundays and company holidays).
            </p>
          </div>
          <button
            type="button"
            className="sdic-btn sdic-btn-secondary"
            onClick={() => {
              const next = !dark;
              setDark(next);
              localStorage.setItem("sdic-dark", next ? "1" : "0");
            }}
          >
            {dark ? "Light" : "Dark"}
          </button>
        </header>

        <SalesImportNav active="items-on-sale" showBranchFilter={false} />

        <section className="sdic-panel ios-filters">
          <div className="ios-filter-grid">
            <label>
              Year
              <select
                value={draft.year}
                onChange={(e) => setDraft((p) => ({ ...p, year: Number(e.target.value) }))}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </label>
            <label>
              Month
              <select
                value={draft.monthIndex}
                onChange={(e) => setDraft((p) => ({ ...p, monthIndex: Number(e.target.value) }))}
              >
                {MONTH_NAMES.map((name, idx) => (
                  <option key={name} value={idx}>{name}</option>
                ))}
              </select>
            </label>
            <label>
              Branch
              <select
                value={draft.branchId}
                onChange={(e) => setDraft((p) => ({ ...p, branchId: e.target.value }))}
              >
                {SALES_IMPORT_BRANCH_OPTIONS.map((b) => (
                  <option key={b} value={b}>{salesImportBranchDisplayName(b)}</option>
                ))}
              </select>
            </label>
            <label>
              Group
              <select
                value={draft.groupName}
                onChange={(e) => setDraft((p) => ({ ...p, groupName: e.target.value }))}
              >
                <option value={ITEMS_ON_SALE_ALL_GROUPS}>All groups</option>
                {groupOptions.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="ios-filter-actions">
            <button
              type="button"
              className="sdic-btn sdic-btn-primary ios-search-btn"
              onClick={onSearch}
              disabled={loading}
            >
              {loading ? "Loading…" : "Search"}
            </button>
            {report && (
              <button
                type="button"
                className="sdic-btn sdic-btn-secondary"
                onClick={() => exportItemsOnSaleDailyReportExcel(report)}
              >
                Excel
              </button>
            )}
          </div>
          <p className="ios-search-hint">
            {loading
              ? "Loading sale lines for the selected month…"
              : "Change Year / Month / Branch / Group, then click Search to refresh."}
          </p>
        </section>

        {report && (
          <>
            <section className="sdic-panel ios-summary">
              <div className="ios-summary-grid">
                <div><dt>Month</dt><dd>{report.meta.monthLabel}</dd></div>
                <div><dt>Branch</dt><dd>{report.meta.branchDisplayName}</dd></div>
                <div><dt>Group</dt><dd>{report.meta.groupLabel}</dd></div>
                <div><dt>Working days</dt><dd>{report.meta.workingDayCount}</dd></div>
                <div><dt>Sundays skipped</dt><dd>{report.meta.sundayCount}</dd></div>
                <div><dt>Holidays skipped</dt><dd>{report.meta.holidayCount}</dd></div>
                <div><dt>Days with sales</dt><dd>{report.meta.grandTotals.workingDaysWithSales}</dd></div>
                <div><dt>All import lines</dt><dd>{(report.meta.scannedLines || 0).toLocaleString()}</dd></div>
                <div><dt>Branch sale lines</dt><dd>{(report.meta.branchSaleCandidates || 0).toLocaleString()}</dd></div>
                <div><dt>On working days</dt><dd>{(report.meta.matchedLines || 0).toLocaleString()}</dd></div>
                <div><dt>Skipped Sundays</dt><dd>{(report.meta.sundaySaleLines || 0).toLocaleString()}</dd></div>
                <div><dt>Total amount</dt><dd>₹ {fm(report.meta.grandTotals.amount)}</dd></div>
              </div>
              {report.meta.scannedLines === 0 && (
                <p className="ios-holiday-note ios-warn">
                  No import data for {report.meta.monthLabel}. Upload that month in Sales Data Import → Import Center, then search again.
                </p>
              )}
              {report.meta.scannedLines > 0 && report.meta.branchSaleCandidates === 0 && (
                <p className="ios-holiday-note ios-warn">
                  Import has {(report.meta.scannedLines || 0).toLocaleString()} lines, but none match this branch’s sale vouchers.
                  {report.meta.voucherTypeSamples?.length
                    ? ` Types found: ${report.meta.voucherTypeSamples.map((v) => `${v.voucherType} (${v.count})`).join(", ")}.`
                    : ""}
                </p>
              )}
              {report.meta.branchSaleCandidates > 0 && report.meta.matchedLines === 0 && (
                <p className="ios-holiday-note ios-warn">
                  Found {report.meta.branchSaleCandidates.toLocaleString()} branch sale lines, but all fell on Sundays/holidays
                  (Sundays: {report.meta.sundaySaleLines || 0}, holidays: {report.meta.holidaySaleLines || 0}).
                </p>
              )}
              {report.skipped?.holidays?.length > 0 && (
                <p className="ios-holiday-note">
                  Excluded holidays:{" "}
                  {report.skipped.holidays.map((h) => `${h.ymd} (${h.name})`).join(", ")}
                </p>
              )}
            </section>

            <section className="sdic-panel ios-day-toolbar">
              <label className="ios-search">
                Filter items
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Part no, item name, group…"
                />
              </label>
              <div className="ios-day-toolbar-actions">
                <button type="button" className="sdic-btn sdic-btn-secondary" onClick={expandAll}>
                  Expand all
                </button>
                <button type="button" className="sdic-btn sdic-btn-secondary" onClick={collapseAll}>
                  Collapse all
                </button>
              </div>
            </section>

            <section className="ios-day-list">
              {filteredDays.map((day) => {
                const open = expandedDays.has(day.ymd);
                const hasItems = day.items.length > 0;
                return (
                  <article key={day.ymd} className={`ios-day-card${open ? " open" : ""}${!hasItems ? " empty" : ""}`}>
                    <button type="button" className="ios-day-head" onClick={() => toggleDay(day.ymd)}>
                      <span className="ios-day-date">{formatItemsOnSaleDayLabel(day)}</span>
                      <span className="ios-day-stats">
                        {hasItems ? (
                          <>
                            {day.totals.itemCount} items · {day.totals.invoiceCount} inv · Qty {fm(day.totals.qty)} · ₹ {fm(day.totals.amount)}
                          </>
                        ) : (
                          "No sales"
                        )}
                      </span>
                      <span className="ios-day-chevron">{open ? "▾" : "▸"}</span>
                    </button>
                    {open && hasItems && (
                      <div className="ios-day-body">
                        <table className="ios-item-table">
                          <thead>
                            <tr>
                              <th>Group</th>
                              <th>Part No</th>
                              <th>Item</th>
                              <th className="num">Qty</th>
                              <th className="num">Amount</th>
                              <th className="num">Invoices</th>
                            </tr>
                          </thead>
                          <tbody>
                            {day.items.map((item) => (
                              <tr key={`${day.ymd}-${item.key}`}>
                                <td>{item.groupName}</td>
                                <td>{item.partNo || "—"}</td>
                                <td>{item.itemName}</td>
                                <td className="num">{fm(item.qty)}</td>
                                <td className="num">{fm(item.amount)}</td>
                                <td className="num">{item.invoiceCount}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </article>
                );
              })}
            </section>
          </>
        )}

        {!report && loading && (
          <section className="sdic-panel ios-empty">
            <p>Loading daily items on sale…</p>
          </section>
        )}

        {!report && !loading && (
          <section className="sdic-panel ios-empty">
            <p>
              Click the <strong>Search</strong> button (blue) under the Year / Month / Branch filters above.
            </p>
            {applied && <p className="text-slate-500">Last search returned no report. Try another month or branch.</p>}
          </section>
        )}
      </main>
    </div>
  );
}
